import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { states, about_approach_data } from "../../utils/data";
import { Target, Heart, Shield, Handshake, Star, TrendingUp } from "lucide-react";
import ContactModal from "../home/hero/ContactModal";
import toast from "react-hot-toast";
import axios from "axios";

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=Syne:wght@300;400;500;600&display=swap');

.ab-root *{box-sizing:border-box;margin:0;padding:0}
.ab-root{
  --ink:#1E2A3A;
  --green:#4E8A3C;
  --green-light:#7BBF6A;
  --green-pale:#EBF5E7;
  --aqua:#7BBCC8;
  --aqua-pale:#E8F4F7;
  --gold:#C9A96E;
  --cream:#F7F6F2;
  --muted:#6B7A8D;
  --border:rgba(30,42,58,0.1);
  --white:#FFFFFF;
  font-family:'Syne',sans-serif;
  color:var(--ink);
  background:var(--white);
  width:100%;
  overflow-x:hidden;
}
.ab-serif{font-family:'Playfair Display',Georgia,serif}

/* PAGE HEADER — warm sage gradient matching hero right */
.ab-header{
  background:linear-gradient(160deg, #C8DFC2 0%, #B8D8D8 50%, #A8C8D4 100%);
  padding:5rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem)) 4.5rem;
  position:relative;overflow:hidden;
}
.ab-header::before{
  content:'';position:absolute;inset:0;
  background:
    radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.4) 0%, transparent 55%),
    radial-gradient(ellipse at 15% 80%, rgba(123,188,200,0.2) 0%, transparent 50%);
  pointer-events:none;
}
.ab-header-inner{position:relative;z-index:1}
.ab-header-eyebrow{font-family:'Syne',sans-serif;font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:rgba(30,42,58,0.5);margin-bottom:0.75rem}
.ab-header h1{font-family:'Playfair Display',serif;font-size:52px;color:var(--ink);font-weight:400;line-height:1.15;max-width:680px}
.ab-header h1 strong{font-style:italic;color:var(--green)}
.ab-header-sub{font-family:'Syne',sans-serif;font-size:14px;color:var(--muted);line-height:1.8;max-width:520px;margin-top:1.25rem;font-weight:300}

/* MISSION */
.ab-mission{padding:5rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem));background:var(--cream)}
.ab-section-eyebrow{font-family:'Syne',sans-serif;font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:var(--green);margin-bottom:0.6rem}
.ab-h2{font-family:'Playfair Display',serif;font-size:32px;color:var(--ink);line-height:1.25;font-weight:400;margin-bottom:2rem}
.ab-mission-grid{display:grid;grid-template-columns:1fr 1fr;gap:4rem;align-items:start}
.ab-mission-text p{font-family:'Syne',sans-serif;font-size:13px;color:var(--muted);line-height:1.85;margin-bottom:1rem;font-weight:300}
.ab-mission-text p:last-child{margin-bottom:0}

.ab-values{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.ab-value{
  display:flex;align-items:center;gap:0.75rem;
  padding:1rem 1.25rem;background:white;
  border:1px solid rgba(78,138,60,0.12);
  border-left:3px solid var(--green);
  border-radius:8px;transition:all 0.2s;
  box-shadow:0 1px 6px rgba(30,42,58,0.04);
}
.ab-value:hover{background:var(--green-pale);transform:translateX(2px)}
.ab-value-icon{width:36px;height:36px;border-radius:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.ab-value-label{font-family:'Syne',sans-serif;font-size:12px;font-weight:500;color:var(--ink)}

/* APPROACH */
.ab-approach{padding:5rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem));background:white}
.ab-approach-header{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:3rem;gap:2rem}
.ab-approach-desc{font-family:'Syne',sans-serif;font-size:13px;color:var(--muted);line-height:1.75;max-width:340px;font-weight:300}
.ab-approach-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:1.5rem}
.ab-approach-card{
  background:white;border:1px solid rgba(78,138,60,0.12);
  border-top:3px solid var(--green);border-radius:10px;
  padding:2rem 1.75rem;transition:all 0.25s;cursor:default;
  box-shadow:0 2px 12px rgba(30,42,58,0.05);
}
.ab-approach-card:hover{background:var(--green-pale);box-shadow:0 6px 24px rgba(78,138,60,0.12);transform:translateY(-2px)}
.ab-approach-num{font-family:'Playfair Display',serif;font-size:36px;color:var(--green);opacity:0.18;line-height:1;margin-bottom:1rem}
.ab-approach-icon{
  width:44px;height:44px;border-radius:8px;
  background:linear-gradient(135deg,var(--green-pale),var(--aqua-pale));
  display:flex;align-items:center;justify-content:center;
  margin-bottom:1.25rem;font-size:20px;
  box-shadow:0 1px 4px rgba(78,138,60,0.12);
}
.ab-approach-card h3{font-family:'Syne',sans-serif;font-size:14px;font-weight:500;color:var(--ink);margin-bottom:0.5rem}
.ab-approach-card p{font-family:'Syne',sans-serif;font-size:12px;color:var(--muted);line-height:1.7;font-weight:300}

/* STATS — matching home CTA gradient */
.ab-stats{
  background:linear-gradient(160deg, #3A5C35 0%, #4A6A60 50%, #3A5870 100%);
  padding:4rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem));
  display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:1px;
  position:relative;overflow:hidden;
}
.ab-stats::before{
  content:'';position:absolute;inset:0;
  background:
    radial-gradient(ellipse at 15% 50%, rgba(200,223,194,0.12) 0%, transparent 55%),
    radial-gradient(ellipse at 85% 30%, rgba(123,188,200,0.1) 0%, transparent 50%);
  pointer-events:none;
}
.ab-stat{padding:2rem;position:relative;z-index:1}
.ab-stat::after{content:'';position:absolute;right:0;top:20%;bottom:20%;width:1px;background:rgba(255,255,255,0.1)}
.ab-stat:last-child::after{display:none}
.ab-stat-num{font-family:'Playfair Display',serif;font-size:42px;color:white;font-weight:400;line-height:1}
.ab-stat-num span{color:var(--green-light)}
.ab-stat-label{font-family:'Syne',sans-serif;font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:rgba(255,255,255,0.4);margin-top:0.5rem}
.ab-stat-desc{font-family:'Syne',sans-serif;font-size:12px;color:rgba(255,255,255,0.3);margin-top:0.4rem;line-height:1.5;font-weight:300}

/* CTA */
.ab-cta{
  padding:4rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem));
  background:var(--cream);
  display:flex;align-items:center;justify-content:space-between;gap:2rem;flex-wrap:wrap;
  border-top:1px solid rgba(78,138,60,0.1);
}
.ab-cta-text .ab-section-eyebrow{margin-bottom:0.4rem}
.ab-cta-text h2{font-family:'Playfair Display',serif;font-size:28px;color:var(--ink);font-weight:400;line-height:1.3}
.ab-cta-text p{font-family:'Syne',sans-serif;font-size:13px;color:var(--muted);margin-top:0.5rem;line-height:1.7;max-width:480px;font-weight:300}
.ab-cta-btns{display:flex;gap:12px;flex-shrink:0;flex-wrap:wrap}
.ab-btn-green{background:var(--green);color:white;font-family:'Syne',sans-serif;font-size:12px;font-weight:500;padding:13px 26px;border-radius:6px;border:none;cursor:pointer;letter-spacing:0.5px;transition:background 0.2s}
.ab-btn-green:hover{background:#3D7030}
.ab-btn-outline{background:transparent;color:var(--ink);font-family:'Syne',sans-serif;font-size:12px;font-weight:500;padding:12px 26px;border-radius:6px;border:1px solid rgba(78,138,60,0.25);cursor:pointer;letter-spacing:0.5px;transition:all 0.2s}
.ab-btn-outline:hover{border-color:var(--green);color:var(--green)}

/* RESPONSIVE */
@media (max-width: 768px) {
  .ab-header{padding:3.5rem 1.5rem}
  .ab-header h1{font-size:34px}
  .ab-mission{padding:3.5rem 1.5rem}
  .ab-mission-grid{grid-template-columns:1fr;gap:2.5rem}
  .ab-h2{font-size:26px}
  .ab-approach{padding:3.5rem 1.5rem}
  .ab-approach-header{flex-direction:column;align-items:flex-start;gap:1rem}
  .ab-approach-desc{max-width:100%}
  .ab-approach-grid{grid-template-columns:1fr}
  .ab-stats{grid-template-columns:1fr;padding:3rem 1.5rem}
  .ab-stat::after{display:none}
  .ab-stat{padding:1.5rem 0;border-bottom:1px solid rgba(255,255,255,0.07)}
  .ab-stat:last-child{border-bottom:none}
  .ab-cta{padding:3rem 1.5rem;flex-direction:column;align-items:flex-start}
}
@media (max-width: 480px) {
  .ab-header{padding:2.5rem 1.25rem}
  .ab-header h1{font-size:28px}
  .ab-mission{padding:3rem 1.25rem}
  .ab-values{grid-template-columns:1fr}
  .ab-approach{padding:3rem 1.25rem}
  .ab-stats{padding:2.5rem 1.25rem}
  .ab-stat-num{font-size:34px}
  .ab-cta{padding:3rem 1.25rem}
  .ab-cta-btns{width:100%}
  .ab-btn-green,.ab-btn-outline{width:100%;text-align:center}
}
@media (min-width: 1440px) {
  .ab-header h1{font-size:62px}
  .ab-h2{font-size:36px}
  .ab-stat-num{font-size:50px}
}
`;

const valueItems = [
  { icon: <Heart size={16} />,      label: "Compassionate Care", bg: "#EBF5E7", color: "#4E8A3C" },
  { icon: <Shield size={16} />,     label: "Trusted Platform",   bg: "#E8F4F7", color: "#4A7A8A" },
  { icon: <Handshake size={16} />,  label: "Partnership",        bg: "#EBF5E7", color: "#3D7030" },
  { icon: <Star size={16} />,       label: "Excellence",         bg: "#FBF5E8", color: "#B8873A" },
  { icon: <TrendingUp size={16} />, label: "Growth",             bg: "#EBF5E7", color: "#4E8A3C" },
  { icon: <Target size={16} />,     label: "Focused Mission",    bg: "#E8F4F7", color: "#7BBCC8" },
];

const About = () => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "", facility: "", city: "", state: "",
    zip: "", phone: "", email: "", question: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ name: "", facility: "", city: "", state: "", zip: "", phone: "", email: "", question: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const { name, email, phone, question, facility, city, state, zip } = formData;
    if (!name || !facility || !city || !state || !zip || !phone || !email || !question) {
      toast.error("Please fill in all required fields.");
      setIsSubmitting(false); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      setIsSubmitting(false); return;
    }
    try {
      await axios.post("https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/contact-us/", formData);
      toast.success("Your message has been sent. We'll get back to you soon!");
      handleClose();
    } catch (error) {
      console.error("Failed to send message:", error.response?.data || error.message);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="ab-root">

        {/* PAGE HEADER */}
        <motion.div
          className="ab-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="ab-header-inner">
            <div className="ab-header-eyebrow">About Us</div>
            <h1 className="ab-serif">
              Built for the next<br />generation of <strong>care.</strong>
            </h1>
            <p className="ab-header-sub">
              ProMed Health Plus equips private practices with the tools, support, and products they need to deliver exceptional wound care — from initial setup through seamless reimbursement.
            </p>
          </div>
        </motion.div>

        {/* MISSION */}
        <section className="ab-mission">
          <div className="ab-mission-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="ab-section-eyebrow">Our Mission</div>
              <h2 className="ab-h2 ab-serif">
                Transforming wound care for independent providers.
              </h2>
              <div className="ab-mission-text">
                <p>
                  At ProMed Health Plus, we are dedicated to transforming wound care by equipping private practices with the tools and support they need to thrive. With over 80% of our services dedicated to empowering independent providers, we guide you from initial setup through seamless reimbursement with expert support at every step.
                </p>
                <p>
                  Our streamlined processes, selective partnerships, and unwavering commitment to excellence ensure superior patient outcomes and practice success.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="ab-values">
                {valueItems.map((v, i) => (
                  <motion.div
                    key={i}
                    className="ab-value"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.07 }}
                  >
                    <div className="ab-value-icon" style={{ background: v.bg, color: v.color }}>
                      {v.icon}
                    </div>
                    <span className="ab-value-label">{v.label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* APPROACH */}
        <section className="ab-approach">
          <div className="ab-approach-header">
            <div>
              <div className="ab-section-eyebrow">Our Approach</div>
              <h2 className="ab-h2 ab-serif" style={{ marginBottom: 0 }}>
                Our approach to<br />wound care solutions.
              </h2>
            </div>
            <p className="ab-approach-desc">
              We combine clinical expertise, smart technology, and dedicated support to deliver outcomes that matter.
            </p>
          </div>

          <div className="ab-approach-grid">
            {about_approach_data.map((item, i) => (
              <motion.div
                key={i}
                className="ab-approach-card"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <div className="ab-approach-num ab-serif">0{i + 1}</div>
                <div className="ab-approach-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* STATS */}
        <div className="ab-stats">
          {[
            { num: "80", suffix: "%",   label: "Independent Providers", desc: "Of our services dedicated to private practices" },
            { num: "24", suffix: "/7",  label: "Clinical Support",      desc: "Round-the-clock access to expert advisory" },
            { num: "1",  suffix: " Day",label: "Response Time",         desc: "A real person responds within one business day" },
          ].map((stat, i) => (
            <motion.div
              key={i}
              className="ab-stat"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <div className="ab-stat-num ab-serif">{stat.num}<span>{stat.suffix}</span></div>
              <div className="ab-stat-label">{stat.label}</div>
              <div className="ab-stat-desc">{stat.desc}</div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="ab-cta">
          <div className="ab-cta-text">
            <div className="ab-section-eyebrow">Get Started</div>
            <h2 className="ab-serif">Ready to work with us?</h2>
            <p>Reach out and our team will match you with the right solution and walk you through every step.</p>
          </div>
          <div className="ab-cta-btns">
            <button className="ab-btn-green" onClick={() => setOpen(true)}>Talk to Our Team</button>
            <button className="ab-btn-outline" onClick={() => setOpen(true)}>Request Info</button>
          </div>
        </div>

      </div>

      <AnimatePresence>
        {open && (
          <ContactModal
            open={open}
            handleClose={handleClose}
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default About;